import { TourConfig, TourHandle, useFeatureTour } from '@app/core/tours/useFeatureTour';
import { defineMessage, useIntl } from 'react-intl';
import { useNotificationsTourDemoStore } from './notifications-tour-demo.store';

/** Anchors the notifications tour to the sidenav bell button. */
export const SIDENAV_NOTIFICATIONS_TOUR_BELL_ID = 'sidenav-notifications-btn';

/** Anchors the notifications tour to the feed list panel. */
export const NOTIFICATIONS_FEED_TOUR_LIST_ID = 'notifications-feed-list';

export type UseNotificationsTourParams = {
  /** Opens or closes the notifications popover (must also set anchor when opening). */
  setOpen: (open: boolean) => void;
};

/**
 * Runs the one-time two-step tour for the notifications center if not yet seen and before expiry.
 * Step 1 highlights the bell; Next opens the panel (with a local demo feed) and advances to step 2.
 */
export function useNotificationsTour({ setOpen }: UseNotificationsTourParams): TourHandle {
  const { $t } = useIntl();
  const setTourDemoActive = useNotificationsTourDemoStore((state) => state.setActive);

  return useFeatureTour({ key: 'sidenavNotifications', expiry: '2027-03-31' }, {
    onDestroyed: () => {
      setTourDemoActive(false);
    },
    steps: [
      {
        element: `#${SIDENAV_NOTIFICATIONS_TOUR_BELL_ID}`,
        popover: {
          title: $t(
            defineMessage({
              id: 'notifications.tour.bell.title',
              defaultMessage: 'Centro de notificaciones',
            }),
          ),
          description: $t(
            defineMessage({
              id: 'notifications.tour.bell.description',
              defaultMessage:
                'Centralizamos el estado de tus exports, reportes y alertas del sistema. Hacé clic en la campana para revisar tu actividad reciente.',
            }),
          ),
          side: 'right',
          align: 'start',
          onNextClick: (_el, _step, { driver }) => {
            // Enable before open so the feed mounts with demo mode already on.
            setTourDemoActive(true);
            setOpen(true);
            // Allow Popover + feed to mount before highlighting the list.
            window.setTimeout(() => driver.moveNext(), 100);
          },
        },
      },
      {
        element: `#${NOTIFICATIONS_FEED_TOUR_LIST_ID}`,
        popover: {
          title: $t(
            defineMessage({
              id: 'notifications.tour.feed.title',
              defaultMessage: 'Gestioná tus notificaciones',
            }),
          ),
          description: $t(
            defineMessage({
              id: 'notifications.tour.feed.description',
              defaultMessage:
                'Filtrá, leé o archivá tus alertas desde acá. Usá el ícono de engranaje (⚙️) para personalizar qué avisos querés recibir.',
            }),
          ),
          side: 'left',
          align: 'start',
        },
      },
    ],
  } satisfies TourConfig);
}
